/**
 * Auth Store
 *
 * Base authentication state management
 * This store is extended by module-auth for OTP/phone validation features
 *
 * Web uses NextAuth for session management, this store syncs with NextAuth session
 */

import { type Observable } from '@legendapp/state';
import { createPersistedStore } from '../../stores/store.setup';
import type { CustomerToken, CustomerProfile } from '../models';

/**
 * Auth Store state interface
 * Matches mobile's AuthStore for compatibility
 */
export type AuthStore = {
    customerToken?: CustomerToken;
    customerProfile?: CustomerProfile;
    isAuthenticated?: boolean;
    isLoading?: boolean;
    error?: string;
};

/**
 * Auth store observable with automatic IndexedDB persistence
 *
 * ✅ No manual SSR checks needed
 * ✅ Automatic persistence on client-side
 * ✅ Works as regular observable on server-side
 *
 * This store should be located in module-core and extended by module-auth
 * On web, it syncs with NextAuth session
 */
export const authStore$: Observable<AuthStore> = createPersistedStore<AuthStore>('auth', {
    customerToken: undefined,
    customerProfile: undefined,
    isAuthenticated: false,
    isLoading: false,
    error: undefined,
});

/**
 * Clear all auth data - used on logout
 */
export const clearAuthData = (): void => {
    authStore$.customerToken.set(undefined);
    authStore$.customerProfile.set(undefined);
    authStore$.isAuthenticated.set(false);
    authStore$.isLoading.set(false);
    authStore$.error.set(undefined);
};
